import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildChatRequestPayload,
  clearChatState,
  createSessionId,
  createWelcomeMessage,
  EMPTY_CONVERSATION_STATE,
  EMPTY_FILTERS,
  getStoredChatState,
  isCurrentChatRequest,
  isDefinitiveChatClientError,
  isValidSessionId,
  normalizeConversationState,
  normalizeFilters,
  saveChatState,
  shouldRotateForSessionClaim,
  STORAGE_VERSION,
} from './chatbotSession.js';

const renderMarkdown = (text) =>
  DOMPurify.sanitize(marked.parse(text || '', { async: false }));

const ENDPOINT = '/api/chatbot.php';
const CHATBOT_OPEN_EVENT = 'chatbot:open';
const DIALOG_TITLE_ID = 'chatbot-dialog-title';
const AI_DISCLOSURE_ID = 'chatbot-ai-disclosure';
const AI_DISCLOSURE_DESCRIPTION_ID = 'chatbot-ai-disclosure-description';
const SESSION_CLAIM_CHANNEL = 'stage4eu_chatbot_session_claims';


/**
 * ---------------------------------------------------------------------------
 * Config protezione lato frontend
 * ---------------------------------------------------------------------------
 * Questi limiti NON sono una barriera di sicurezza: l'utente può sempre
 * bypassare React e chiamare /api/chatbot.php direttamente.
 * Servono però a migliorare UX, ridurre invii accidentali e dare feedback
 * immediato prima ancora che il backend risponda.
 *
 * MAX_DRAFT_LENGTH
 * - Deve restare allineato al message length cap del PHP.
 * - Default 700 caratteri: sufficiente per una richiesta stage sensata.
 *
 * MAX_USER_MESSAGES_PER_SESSION
 * - Limite soft per la sessione browser corrente.
 * - Il limite vero resta il rate limit server-side in chatbot.php.
 */
const MAX_DRAFT_LENGTH = 700;
const MAX_USER_MESSAGES_PER_SESSION = 60;
const SCROLL_TOP_OFFSET = 32;

const getOfferAreasText = (offer) => {

  if (!Array.isArray(offer?.professional_areas)) {
    return '';
  }

  return offer.professional_areas
    .map((area) => {
      if (typeof area === 'string') {
        return `<span class="badge fw-light fs-13 rounded-pill text-dark mb-1">${area}</span >`;
      }

      if (area && typeof area === 'object') {
        return `<span class="badge fw-light fs-13 rounded-pill text-dark mb-1">${area.text || area.name || ''
          }</span >`;
      }

      return '';
    })
    .filter(Boolean)
    .join('  ');
};

/**
 * Estrae le CTA racchiuse nei tag <ctas><cta>...</cta></ctas>
 * Restituisce il testo ripulito dai tag e un array contenente fino a 3 CTA.
 */
const parseCtas = (text) => {
  const ctaBlockRegex = /<ctas>([\s\S]*?)<\/ctas>/i;
  const match = ctaBlockRegex.exec(text || '');
  if (!match) {
    return { cleanText: text || '', ctas: [] };
  }

  const cleanText = text.replace(ctaBlockRegex, '').trim();
  const rawCtas = match[1];

  const ctaRegex = /<cta>([\s\S]*?)<\/cta>/gi;
  const ctas = [];
  let ctaMatch;

  while ((ctaMatch = ctaRegex.exec(rawCtas)) !== null) {
    const ctaText = ctaMatch[1].trim();
    if (ctaText && ctaText.length <= 45) {
      ctas.push(ctaText);
    }
  }

  return { cleanText, ctas: ctas.slice(0, 3) };
};

const getPostOffersMessage = (payload) => {
  if (typeof payload?.post_offers_message !== 'string') {
    return '';
  }

  return payload.post_offers_message.trim();
};

const normalizePromptText = (text) =>
  (text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n');

const truncatePromptText = (text) => {
  if (text.length <= MAX_DRAFT_LENGTH) {
    return text;
  }

  return text.slice(0, MAX_DRAFT_LENGTH);
};

const getEditableText = (element) => {
  if (!element) {
    return '';
  }

  return normalizePromptText(element.innerText || '').replace(/\n$/u, '');
};

const setEditableText = (element, text) => {
  if (!element) {
    return;
  }

  element.textContent = text || '';
};

const moveCaretToEnd = (element) => {
  if (!element || typeof window === 'undefined') {
    return;
  }

  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  const range = document.createRange();

  range.selectNodeContents(element);
  range.collapse(false);

  selection.removeAllRanges();
  selection.addRange(range);
};

const insertPlainTextAtSelection = (container, text) => {
  if (!container || typeof window === 'undefined') {
    return;
  }

  const selection = window.getSelection();

  if (!selection || selection.rangeCount === 0) {
    container.textContent = `${container.textContent || ''}${text}`;
    moveCaretToEnd(container);
    return;
  }

  const range = selection.getRangeAt(0);

  if (!container.contains(range.commonAncestorContainer)) {
    container.textContent = `${container.textContent || ''}${text}`;
    moveCaretToEnd(container);
    return;
  }

  range.deleteContents();

  const textNode = document.createTextNode(text);
  range.insertNode(textNode);

  range.setStartAfter(textNode);
  range.setEndAfter(textNode);

  selection.removeAllRanges();
  selection.addRange(range);
};

const ChatBot = () => {
  const initialChatState = useMemo(() => getStoredChatState(), []);

  const [isOpen, setIsOpen] = useState(initialChatState?.isOpen || false);
  const [isSending, setIsSending] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [draft, setDraft] = useState('');
  const [sessionId, setSessionId] = useState(() =>
    initialChatState?.sessionId || createSessionId()
  );
  const [activeFilters, setActiveFilters] = useState(
    initialChatState?.activeFilters || EMPTY_FILTERS
  );
  const [conversationState, setConversationState] = useState(
    initialChatState?.conversationState || EMPTY_CONVERSATION_STATE
  );
  const [messages, setMessages] = useState(
    initialChatState?.messages || [createWelcomeMessage()]
  );

  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(null);
  const lastUserMessageIdRef = useRef(null);
  const pendingScrollMessageIdRef = useRef(null);
  const scrollObserverRef = useRef(null);
  const scrollTimeoutsRef = useRef([]);
  const isComposingRef = useRef(false);
  const isSendingRef = useRef(false);
  const requestControllerRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const conversationGenerationRef = useRef(0);
  const tabInstanceRef = useRef({
    id: createSessionId(),
    startedAt: Date.now(),
  });

  const syncPromptDom = (value, shouldFocus = false) => {
    const inputElement = inputRef.current;

    if (!inputElement) {
      return;
    }

    setEditableText(inputElement, value);

    if (shouldFocus) {
      inputElement.focus();
      moveCaretToEnd(inputElement);
    }
  };

  const setPromptValue = (value, shouldFocus = false) => {
    const normalized = truncatePromptText(normalizePromptText(value));

    setDraft(normalized);
    syncPromptDom(normalized, shouldFocus);
  };

  const handleCtaClick = (ctaText) => {
    setPromptValue(ctaText, true);
  };

  const clearScheduledMessageScroll = () => {
    scrollTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });

    scrollTimeoutsRef.current = [];

    if (scrollObserverRef.current) {
      scrollObserverRef.current.disconnect();
      scrollObserverRef.current = null;
    }
  };

  const getMessageElementById = (messageId) => {
    const container = messagesRef.current;

    if (!container || !messageId) {
      return null;
    }

    return Array.from(
      container.querySelectorAll('[data-chatbot-message-id]')
    ).find((element) => {
      return element.dataset.chatbotMessageId === messageId;
    });
  };

  const scrollToMessageStart = (messageId, behavior = 'smooth') => {
    const container = messagesRef.current;
    const messageElement = getMessageElementById(messageId);

    if (!container || !messageElement) {
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();

    const nextScrollTop =
      container.scrollTop +
      (messageRect.top - containerRect.top) -
      SCROLL_TOP_OFFSET;

    container.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior,
    });

    return true;
  };

  const scheduleScrollToMessageStart = (messageId) => {
    if (!messageId || typeof window === 'undefined') {
      return;
    }

    const container = messagesRef.current;

    if (!container) {
      return;
    }

    clearScheduledMessageScroll();

    const runScroll = () => {
      scrollToMessageStart(messageId);
    };

    window.requestAnimationFrame(() => {
      runScroll();

      window.requestAnimationFrame(() => {
        runScroll();
      });
    });

    [80, 180, 360, 700].forEach((delay) => {
      const timeoutId = window.setTimeout(runScroll, delay);
      scrollTimeoutsRef.current.push(timeoutId);
    });

    scrollObserverRef.current = new MutationObserver(() => {
      runScroll();
    });

    scrollObserverRef.current.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const disconnectTimeoutId = window.setTimeout(() => {
      if (scrollObserverRef.current) {
        scrollObserverRef.current.disconnect();
        scrollObserverRef.current = null;
      }
    }, 900);

    scrollTimeoutsRef.current.push(disconnectTimeoutId);
  };

  const queueScrollToUserMessage = (messageId) => {
    pendingScrollMessageIdRef.current = messageId;
  };

  useEffect(() => {
    saveChatState({
      version: STORAGE_VERSION,
      sessionId,
      isOpen,
      messages,
      activeFilters,
      conversationState,
      pendingRequest: hasPendingRequest,
    });
  }, [
    isOpen,
    messages,
    sessionId,
    activeFilters,
    conversationState,
    hasPendingRequest,
  ]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    if (typeof window.BroadcastChannel !== 'function') {
      return undefined;
    }

    const channel = new window.BroadcastChannel(SESSION_CLAIM_CHANNEL);
    const localInstance = tabInstanceRef.current;

    const postClaim = () => {
      channel.postMessage({
        type: 'claim',
        instanceId: localInstance.id,
        startedAt: localInstance.startedAt,
        sessionId: sessionIdRef.current,
      });
    };

    channel.onmessage = (event) => {
      const claim = event?.data;

      if (
        !claim ||
        claim.type !== 'claim' ||
        claim.instanceId === localInstance.id ||
        !isValidSessionId(claim.sessionId) ||
        claim.sessionId !== sessionIdRef.current ||
        typeof claim.startedAt !== 'number'
      ) {
        return;
      }

      if (!shouldRotateForSessionClaim(localInstance, {
        id: claim.instanceId,
        startedAt: claim.startedAt,
      })) {
        // Il tab più vecchio mantiene la conversazione e risponde al clone.
        postClaim();
        return;
      }

      // sessionStorage può essere clonato duplicando un tab. Il clone più
      // recente riparte pulito, senza condividere la history AnythingLLM.
      const isolatedSessionId = createSessionId();
      conversationGenerationRef.current += 1;
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
      isSendingRef.current = false;
      sessionIdRef.current = isolatedSessionId;
      lastUserMessageIdRef.current = null;
      pendingScrollMessageIdRef.current = null;
      clearScheduledMessageScroll();
      clearChatState();
      setSessionId(isolatedSessionId);
      setMessages([createWelcomeMessage()]);
      setActiveFilters(EMPTY_FILTERS);
      setConversationState(EMPTY_CONVERSATION_STATE);
      setHasPendingRequest(false);
      setPromptValue('');
      setIsSending(false);
      postClaim();
    };

    const initialClaimTimeout = window.setTimeout(postClaim, 0);

    return () => {
      window.clearTimeout(initialClaimTimeout);
      channel.close();
    };
  }, []);

  useEffect(() => {
    const openModal = () => {
      setIsOpen(true);
    };

    window.addEventListener(CHATBOT_OPEN_EVENT, openModal);

    return () => {
      window.removeEventListener(CHATBOT_OPEN_EVENT, openModal);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      moveCaretToEnd(inputRef.current);
    });

    const onEsc = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', onEsc);

    return () => {
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);


  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const inputElement = inputRef.current;

    if (!inputElement) {
      return;
    }

    const currentText = getEditableText(inputElement);

    if (currentText !== draft) {
      setEditableText(inputElement, draft);
    }
  }, [isOpen, draft]);

  useEffect(() => {
    const messageId = pendingScrollMessageIdRef.current;

    if (!messageId) {
      return;
    }

    pendingScrollMessageIdRef.current = null;
    scheduleScrollToMessageStart(messageId);
  }, [messages]);

  useEffect(() => {
    return () => {
      conversationGenerationRef.current += 1;
      isSendingRef.current = false;
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
      clearScheduledMessageScroll();
    };
  }, []);

  const removeSkeletonThen = (updater) => {
    setMessages((previous) =>
      updater(previous.filter((message) => message.id !== 'skeleton'))
    );
  };

  const appendErrorMessage = (sessionWasIsolated = false) => {
    queueScrollToUserMessage(lastUserMessageIdRef.current);

    removeSkeletonThen((previous) => [
      ...previous,
      {
        id: `bot-error-${Date.now()}`,
        role: 'bot',
        type: 'text',
        text: sessionWasIsolated
          ? 'La risposta non è arrivata in modo completo. Per evitare memoria invisibile ho isolato la conversazione AI: ripeti la richiesta includendo il contesto necessario.'
          : 'Si è verificato un problema nel recupero della risposta. Riprova tra poco.',
      },
    ]);
  };

  /**
   * -------------------------------------------------------------------------
   * appendApiResponse
   * -------------------------------------------------------------------------
   * Gestisce sia il vecchio contratto:
   * - reply
   * - offers
   * - calls
   *
   * sia il nuovo contratto a doppia risposta:
   * - anything_reply: testo conversazionale generato da AnythingLLM;
   * - reply: testo sintetico Stage4EU/Joomla;
   * - offers: card offerte;
   * - calls: card bandi e programmi;
   * - post_offers_message: messaggio di raffinamento da mostrare sotto la griglia.
   *
   * Ordine visuale:
   * 1. anything_reply, se presente;
   * 2. reply Stage4EU, se presente e non duplicata;
   * 3. offers, se presenti;
   * 4. calls, se presenti, in una sezione distinta;
   * 5. post_offers_message, se presente, come messaggio bot dopo i risultati.
   */
  const appendApiResponse = (payload) => {
    const anythingReply =
      typeof payload?.anything_reply === 'string'
        ? payload.anything_reply.trim()
        : '';

    const replyText = (
      payload?.reply ||
      payload?.message ||
      payload?.text ||
      ''
    ).trim();

    const postOffersMessage = getPostOffersMessage(payload);

    const fallbackReply =
      'Ho ricevuto la richiesta ma non ci sono dettagli da mostrarti.';

    queueScrollToUserMessage(lastUserMessageIdRef.current);

    removeSkeletonThen((previous) => {
      const nextMessages = [...previous];
      const now = Date.now();

      if (anythingReply) {
        nextMessages.push({
          id: `bot-anything-reply-${now}`,
          role: 'bot',
          type: 'text',
          text: anythingReply,
        });
      }

      /**
       * Mostriamo la reply Stage4EU se:
       * - esiste;
       * - non è identica ad anything_reply.
       *
       * Se AnythingLLM e reply coincidono, evitiamo doppioni brutti.
       */
      if (replyText && replyText !== anythingReply) {
        nextMessages.push({
          id: `bot-stage4eu-reply-${now}`,
          role: 'bot',
          type: 'text',
          text: replyText,
        });
      }

      if (!anythingReply && !replyText) {
        nextMessages.push({
          id: `bot-fallback-reply-${now}`,
          role: 'bot',
          type: 'text',
          text: fallbackReply,
        });
      }

      if (Array.isArray(payload?.offers) && payload.offers.length > 0) {
        nextMessages.push({
          id: `bot-offers-${now}`,
          role: 'bot',
          type: 'offers',
          offers: payload.offers,
        });
      }

      if (Array.isArray(payload?.calls) && payload.calls.length > 0) {
        nextMessages.push({
          id: `bot-calls-${now}`,
          role: 'bot',
          type: 'calls',
          calls: payload.calls,
        });
      }

      if (postOffersMessage) {
        nextMessages.push({
          id: `bot-post-offers-message-${now}`,
          role: 'bot',
          type: 'text',
          text: postOffersMessage,
        });
      }

      return nextMessages;
    });
  };

  const resetChat = () => {
    const previousSessionId = sessionIdRef.current;
    const nextSessionId = createSessionId();

    conversationGenerationRef.current += 1;
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    isSendingRef.current = false;
    sessionIdRef.current = nextSessionId;
    lastUserMessageIdRef.current = null;
    pendingScrollMessageIdRef.current = null;
    clearScheduledMessageScroll();

    setMessages([createWelcomeMessage()]);
    setSessionId(nextSessionId);
    setActiveFilters(EMPTY_FILTERS);
    setConversationState(EMPTY_CONVERSATION_STATE);
    setHasPendingRequest(false);
    setPromptValue('');
    setIsSending(false);
    clearChatState();

    // La rotazione isola immediatamente la nuova chat. Il reset upstream è
    // best-effort e serve a invalidare anche la memoria persistita del vecchio ID.
    axios
      .post(
        ENDPOINT,
        {
          session_id: previousSessionId,
          reset: true,
        },
        {
          headers: { Accept: 'application/json' },
          timeout: 15000,
        }
      )
      .catch((error) => {
        console.warn('Unable to reset previous chatbot session', error);
      });

    /**
     * Restituiamo il focus all'input:
     * UX piccola, impatto grande.
     */
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      moveCaretToEnd(inputRef.current);
    });
  };

  const handlePromptInput = () => {
    const inputElement = inputRef.current;

    if (!inputElement) {
      return;
    }

    let nextDraft = getEditableText(inputElement);

    if (nextDraft.length > MAX_DRAFT_LENGTH) {
      nextDraft = truncatePromptText(nextDraft);
      setEditableText(inputElement, nextDraft);
      moveCaretToEnd(inputElement);
    } else if (nextDraft === '') {
      setEditableText(inputElement, '');
    }

    setDraft(nextDraft);
  };

  const handlePromptBeforeInput = (event) => {
    if (isSending) {
      event.preventDefault();
      return;
    }

    if (
      typeof event.inputType === 'string' &&
      event.inputType.startsWith('delete')
    ) {
      return;
    }

    const currentText = draft || '';
    const incomingText = event.data || '';

    if (
      incomingText &&
      currentText.length >= MAX_DRAFT_LENGTH &&
      window.getSelection()?.toString() === ''
    ) {
      event.preventDefault();
    }
  };

  const handlePromptPaste = (event) => {
    if (isSending) {
      event.preventDefault();
      return;
    }

    const plainText = event.clipboardData?.getData('text/plain') || '';

    if (!plainText) {
      return;
    }

    event.preventDefault();

    insertPlainTextAtSelection(inputRef.current, plainText);

    let nextDraft = getEditableText(inputRef.current);
    nextDraft = truncatePromptText(nextDraft);

    setEditableText(inputRef.current, nextDraft);
    moveCaretToEnd(inputRef.current);
    setDraft(nextDraft);
  };

  const handlePromptKeyDown = (event) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !isComposingRef.current
    ) {
      event.preventDefault();

      if (!isSending && draft.trim()) {
        event.currentTarget.closest('form')?.requestSubmit();
      }
    }
  };

  const handlePromptCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handlePromptCompositionEnd = () => {
    isComposingRef.current = false;
  };

  const handleSend = async (event) => {
    event.preventDefault();

    const trimmed = draft.trim();

    if (!trimmed || isSendingRef.current) {
      return;
    }

    if (trimmed.length > MAX_DRAFT_LENGTH) {
      setMessages((previous) => [
        ...previous,
        {
          id: `bot-message-too-long-${Date.now()}`,
          role: 'bot',
          type: 'text',
          text: `Il messaggio è troppo lungo. Scrivimi una richiesta più breve, massimo ${MAX_DRAFT_LENGTH} caratteri.`,
        },
      ]);
      return;
    }

    const userMessagesCount = messages.filter(
      (message) => message.role === 'user'
    ).length;

    if (userMessagesCount >= MAX_USER_MESSAGES_PER_SESSION) {
      setMessages((previous) => [
        ...previous,
        {
          id: `bot-session-limit-${Date.now()}`,
          role: 'bot',
          type: 'text',
          text: 'Hai raggiunto il limite massimo di messaggi per questa sessione. Avvia una nuova chat o riprova più tardi.',
        },
      ]);
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      type: 'text',
      text: trimmed,
    };

    let requestSessionId = sessionIdRef.current;
    let requestConversationState = conversationState;
    const requestGeneration = conversationGenerationRef.current;
    const requestController = new AbortController();
    const pendingMessages = [
      ...messages,
      userMessage,
      {
        id: 'skeleton',
        role: 'bot',
        type: 'skeleton',
      },
    ];

    lastUserMessageIdRef.current = userMessage.id;
    queueScrollToUserMessage(userMessage.id);

    setMessages(pendingMessages);

    setPromptValue('');
    isSendingRef.current = true;
    setIsSending(true);
    setHasPendingRequest(true);
    requestControllerRef.current = requestController;

    // Il marker viene scritto prima della POST, non soltanto dal prossimo
    // useEffect. Un refresh immediato non può quindi riusare una sessione il cui
    // ultimo turno potrebbe essere già stato memorizzato da AnythingLLM.
    const pendingStateSaved = saveChatState({
      version: STORAGE_VERSION,
      sessionId: requestSessionId,
      isOpen,
      messages: pendingMessages,
      activeFilters,
      conversationState,
      pendingRequest: true,
    });

    if (!pendingStateSaved) {
      // Se il marker non è persistibile, il turno usa un ID nuovo e isolato.
      // Un eventuale record vecchio non potrà quindi riaprire questa richiesta
      // come memoria invisibile dopo un refresh.
      clearChatState();
      requestSessionId = createSessionId();
      requestConversationState = EMPTY_CONVERSATION_STATE;
      sessionIdRef.current = requestSessionId;
      setSessionId(requestSessionId);
      setConversationState(EMPTY_CONVERSATION_STATE);

      saveChatState({
        version: STORAGE_VERSION,
        sessionId: requestSessionId,
        isOpen,
        messages: pendingMessages,
        activeFilters,
        conversationState: requestConversationState,
        pendingRequest: true,
      });
    }

    try {
      const { data: payload } = await axios.post(
        ENDPOINT,
        buildChatRequestPayload(
          trimmed,
          requestSessionId,
          requestConversationState,
          activeFilters
        ),
        {
          headers: {
            Accept: 'application/json',
          },
          signal: requestController.signal,
        }
      );

      if (!isCurrentChatRequest(
        requestGeneration,
        conversationGenerationRef.current,
        requestSessionId,
        sessionIdRef.current
      )) {
        return;
      }

      appendApiResponse(payload);
      setConversationState(
        normalizeConversationState(
          payload?.conversation_state,
          payload?.response_mode
        )
      );

      if (payload?.filters) {
        setActiveFilters(normalizeFilters(payload.filters));
      }
    } catch (error) {
      if (
        axios.isCancel(error) ||
        requestController.signal.aborted ||
        !isCurrentChatRequest(
          requestGeneration,
          conversationGenerationRef.current,
          requestSessionId,
          sessionIdRef.current
        )
      ) {
        return;
      }

      console.error('ChatBot request failed', error);

      const backendPayload = error?.response?.data;
      const responseStatus = error?.response?.status;
      const hasDefinitiveClientError = isDefinitiveChatClientError(responseStatus);
      const backendReply = backendPayload?.reply;
      const backendAnythingReply = backendPayload?.anything_reply;

      if (
        (typeof backendReply === 'string' && backendReply.trim() !== '') ||
        (typeof backendAnythingReply === 'string' &&
          backendAnythingReply.trim() !== '')
      ) {
        appendApiResponse({
          reply: backendReply,
          anything_reply: backendAnythingReply,
          offers: Array.isArray(backendPayload?.offers)
            ? backendPayload.offers
            : [],
          calls: Array.isArray(backendPayload?.calls)
            ? backendPayload.calls
            : [],
          post_offers_message: backendPayload?.post_offers_message,
          filters: backendPayload?.filters,
        });
      } else {
        appendErrorMessage(!hasDefinitiveClientError);
      }

      if (!hasDefinitiveClientError) {
        // Una disconnessione o un 5xx può arrivare dopo che AnythingLLM ha già
        // salvato il turno. Abbandoniamo quell'ID: un retry non potrà duplicare
        // o riaprire una risposta che l'utente non ha mai visto.
        const isolatedSessionId = createSessionId();
        conversationGenerationRef.current += 1;
        sessionIdRef.current = isolatedSessionId;
        requestControllerRef.current = null;
        isSendingRef.current = false;
        clearChatState();
        setSessionId(isolatedSessionId);
        setConversationState(EMPTY_CONVERSATION_STATE);
        setHasPendingRequest(false);
        setIsSending(false);

        window.requestAnimationFrame(() => {
          inputRef.current?.focus();
          moveCaretToEnd(inputRef.current);
        });
      }
    } finally {
      if (requestControllerRef.current === requestController) {
        requestControllerRef.current = null;
      }

      if (isCurrentChatRequest(
        requestGeneration,
        conversationGenerationRef.current,
        requestSessionId,
        sessionIdRef.current
      )) {
        isSendingRef.current = false;
        setIsSending(false);
        setHasPendingRequest(false);

        window.requestAnimationFrame(() => {
          inputRef.current?.focus();
          moveCaretToEnd(inputRef.current);
        });
      }
    }
  };

  const renderOfferCard = (offer, index) => {
    const title = offer?.title || 'Offerta di stage';
    const location = offer?.location || offer?.place || offer?.country || '';
    const date = offer?.date || offer?.publishedAt || '';
    const dateIso = offer?.date_iso || '';
    const url = offer?.url || offer?.link || '#';
    const areasText = getOfferAreasText(offer);

    return (
      <article key={`${offer?.id || title}-${index}`} className="offer-card">
        {date ? (
          <time className="offer-card__date d-none" dateTime={dateIso || date}>
            {date}
          </time>
        ) : null}

        <h3 className="offer-card__title">
          <a
            href={url}
            target={url.startsWith('http') ? '_blank' : undefined}
            rel={url.startsWith('http') ? 'noopener noreferrer' : undefined}
          >
            {title}
          </a>
        </h3>

        {location ? (
          <p className="offer-card__location">{location}</p>
        ) : null}

        {areasText ? (
          <p
            className="offer-card__areas lh-sm fs-11 mt-2 mb-0"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(areasText) }}
          />
        ) : null}

        <a
          className="offer-card__link border-top border-1 border-gray pt-2"
          href={url}
          target={url.startsWith('http') ? '_blank' : undefined}
          rel={url.startsWith('http') ? 'noopener noreferrer' : undefined}
          data-focus-mouse="false"
        >
          Vedi l&apos;offerta
        </a>
      </article>
    );
  };

  const renderCallCard = (call, index) => {
    const title = call?.title || 'Bando o programma di tirocinio';
    const summary = call?.summary || '';
    const date = call?.date || '';
    const dateIso = call?.date_iso || '';
    const deadlineEvidence = call?.deadline_evidence || '';
    const url = call?.url || '#';
    const opensNewWindow = url.startsWith('http');

    return (
      <article
        key={`${call?.id || title}-${index}`}
        className="chatbot-call-card"
      >
        <span className="chatbot-call-card__status">
          Pubblicato su Stage4eu
        </span>

        <h3 className="chatbot-call-card__title">
          <a
            href={url}
            target={opensNewWindow ? '_blank' : undefined}
            rel={opensNewWindow ? 'noopener noreferrer' : undefined}
          >
            {title}
          </a>
        </h3>

        {summary ? (
          <p className="chatbot-call-card__summary">{summary}</p>
        ) : null}

        {deadlineEvidence ? (
          <p className="chatbot-call-card__deadline">
            <strong>Scadenza indicata nella scheda:</strong>{' '}
            {deadlineEvidence}
          </p>
        ) : null}

        {date ? (
          <p className="chatbot-call-card__updated">
            Scheda aggiornata il{' '}
            <time dateTime={dateIso || date}>{date}</time>
          </p>
        ) : null}

        <a
          className="chatbot-call-card__link"
          href={url}
          target={opensNewWindow ? '_blank' : undefined}
          rel={opensNewWindow ? 'noopener noreferrer' : undefined}
        >
          Apri la scheda del bando
          <span className="visually-hidden">
            {opensNewWindow ? ' (si apre in una nuova finestra)' : ''}
          </span>
        </a>
      </article>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="chatbot-modal"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          setIsOpen(false);
        }
      }}
    >
      <section
        ref={dialogRef}
        className="chatbot-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_TITLE_ID}
        aria-describedby={`${AI_DISCLOSURE_ID} ${AI_DISCLOSURE_DESCRIPTION_ID}`}
      >
        <header className="chatbot-modal__header d-flex align-items-center justify-content-center position-relative">
          <h2
            id={DIALOG_TITLE_ID}
            className="chatbot-modal__title mb-0 text-start text-md-center"
          >
            Stage4eu Assistant: sistema di intelligenza artificiale
          </h2>

          <div className="chatbot-modal__actions d-flex align-items-center gap-2">
            <button
              type="button"
              className="chatbot-modal__reset btn bg-light"
              aria-label="Avvia una nuova chat"
              title="Avvia una nuova chat"
              onClick={resetChat}
            >
              <i className="bi bi-chat-left-quote" aria-hidden="true" />
            </button>

            <button
              type="button"
              title="Chiudi"
              className="chatbot-modal__close btn"
              aria-label="Chiudi chat"
              onClick={() => setIsOpen(false)}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
        </header>

        <aside className="chatbot-ai-disclosure" role="note">
          <span className="chatbot-ai-disclosure__icon" aria-hidden="true">
            <i className="bi bi-stars" />
          </span>

          <div>
            <strong id={AI_DISCLOSURE_ID} className="chatbot-ai-disclosure__title">
              Stai interagendo con un sistema di intelligenza artificiale.
            </strong>
            <p
              id={AI_DISCLOSURE_DESCRIPTION_ID}
              className="chatbot-ai-disclosure__text mb-0"
            >
              Le risposte testuali sono generate automaticamente e possono
              contenere errori. Le offerte mostrate provengono dal database
              Stage4eu; lo stesso vale per i bandi e i programmi mostrati nelle
              schede dedicate. Verifica le informazioni importanti e non inserire dati
              personali o sensibili.
            </p>
          </div>
        </aside>

        <div ref={messagesRef} className="chatbot-modal__messages">
          {messages.map((message) => (
            <div
              key={message.id}
              data-chatbot-message-id={message.id}
              data-chatbot-message-role={message.role}
              data-ai-output={
                message.role === 'bot' && message.type === 'text'
                  ? 'true'
                  : undefined
              }
              className={[
                'chatbot-message',
                `chatbot-message--${message.role}`,
                message.type === 'offers' ? 'chatbot-message--offers' : '',
                message.type === 'calls' ? 'chatbot-message--calls' : '',
                message.type === 'skeleton' ? 'chatbot-message--skeleton' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {message.type === 'skeleton' ? (
                <>
                  <span className="chatbot-message__icon" aria-hidden="true">
                    <i className="bi bi-chat-dots" />
                  </span>

                  <div
                    className="chatbot-skeleton__lines"
                    aria-label="Caricamento risposta"
                    role="status"
                  >
                    <div className="chatbot-skeleton__line" />
                    <div className="chatbot-skeleton__line" />
                    <div className="chatbot-skeleton__line" />
                  </div>
                </>
              ) : null}

              {message.type === 'text' ? (
                <>
                  {message.role === 'bot' ? (
                    (() => {
                      const { cleanText, ctas } = parseCtas(message.text);
                      return (
                        <div className="chatbot-message__text-wrapper w-100">
                          <span className="chatbot-message__source-label">
                            Assistente IA
                          </span>
                          <div
                            className="chatbot-message__text mb-0"
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(cleanText),
                            }}
                          />
                          {ctas.length > 0 && (
                            <div className="chatbot-message__ctas d-flex flex-wrap gap-2 mt-3 mb-1">
                              {ctas.map((cta, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="btn btn-outline-primary btn-sm rounded-pill chatbot-cta-pill py-1 px-3 fs-13"
                                  onClick={() => handleCtaClick(cta)}
                                >
                                  {cta}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="chatbot-message__text mb-0">
                      {message.text}
                    </p>
                  )}

                  <span className="chatbot-message__icon" aria-hidden="true">
                    <i
                      className={`bi ${message.role === 'user' ? 'bi-person' : 'bi-chat-dots'
                        }`}
                    />
                  </span>
                </>
              ) : null}


              {message.type === 'offers' ? (
                <div className="chatbot-offers-grid">
                  {message.offers.map((offer, index) =>
                    renderOfferCard(offer, index)
                  )}
                </div>
              ) : null}

              {message.type === 'calls' ? (
                <section
                  className="chatbot-calls-section"
                  aria-labelledby={`${message.id}-title`}
                >
                  <header className="chatbot-calls-section__header">
                    <h2 id={`${message.id}-title`}>Bandi e programmi</h2>
                    <p>
                      Schede attualmente pubblicate nel portale Stage4eu.
                      Verifica nella scheda i termini e le scadenze aggiornate.
                    </p>
                  </header>
                  <div className="chatbot-calls-grid">
                    {message.calls.map((call, index) =>
                      renderCallCard(call, index)
                    )}
                  </div>
                </section>
              ) : null}
            </div>
          ))}
        </div>

        <footer className="chatbot-modal__footer">
          <form className="chatbot-input" onSubmit={handleSend}>
            <label htmlFor="chatbot-input-text" className="visually-hidden">
              Scrivi il tuo messaggio
            </label>

            <div
              id="chatbot-input-text"
              ref={inputRef}
              className="form-control chatbot-input__control"
              role="textbox"
              aria-label="Scrivi il tuo messaggio"
              aria-multiline="true"
              aria-disabled={isSending}
              contentEditable={!isSending}
              suppressContentEditableWarning
              tabIndex={0}
              data-placeholder="Come posso aiutarti?"
              onBeforeInput={handlePromptBeforeInput}
              onInput={handlePromptInput}
              onPaste={handlePromptPaste}
              onDrop={(event) => event.preventDefault()}
              onKeyDown={handlePromptKeyDown}
              onCompositionStart={handlePromptCompositionStart}
              onCompositionEnd={handlePromptCompositionEnd}
            />

            <button
              type="submit"
              className="d-flex align-items-center justify-content-center chatbot-input__submit btn"
              disabled={isSending || !draft.trim()}
              aria-label="Invia richiesta"
            >
              <i className="bi bi-arrow-up" aria-hidden="true" />
            </button>
          </form>
        </footer>
      </section>
    </div>
  );
};

export default ChatBot;
